import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  parseResponse,
  DEFAULT_BACKEND_FETCH_TIMEOUT_MS,
} from "@softmax/observatory-client/transport";
import { withRequestDeadline } from "@softmax/observatory-client/request-signal";
import type { FeedbackViewerStatus } from "@softmax/observatory-client/feedback-types";
import { useDiscardUnavailableData } from "../../data/use-discard-unavailable-data";
import { useBrowserSession } from "../../session/use-browser-session";

export function useFeedbackViewer({
  isDisabledPath,
}: {
  readonly isDisabledPath: boolean;
}) {
  const { session, kind } = useBrowserSession();
  const client = useQueryClient();
  const viewerKey = ["feedback", "viewer", session?.user.id ?? "anonymous"];
  const query = useQuery({
    queryKey: viewerKey,
    queryFn: ({ signal }) =>
      fetch("/api/feedback/viewer", {
        cache: "no-store",
        signal: withRequestDeadline(signal, DEFAULT_BACKEND_FETCH_TIMEOUT_MS),
      }).then((response) => parseResponse<FeedbackViewerStatus>(response)),
    enabled: !isDisabledPath && kind !== "pending" && session !== null,
    staleTime: 5 * 60000,
  });
  const unavailable = useDiscardUnavailableData({
    queryKey: viewerKey,
    error: query.error,
  });
  const revokeLauncher = () =>
    client.setQueryData<FeedbackViewerStatus>(viewerKey, (value) =>
      value ? { ...value, launcherEnabled: false } : value,
    );
  return {
    launcherEnabled: !unavailable && query.data?.launcherEnabled === true,
    revokeLauncher,
  };
}
